import React, { useState, useEffect } from 'react';
import { FileText, PenTool, Clock, Target, Sparkles, RefreshCw } from 'lucide-react';
import { supabase } from '../../../../../lib/supabase';
import { generateEssayRequirements } from '../../utils/dataGeneration';

const EssaysTab = ({ colleges = [], currentUser, portfolioLocked = false, onDataGenerated, onDeleteCollege }) => {
  const [essayData, setEssayData] = useState({});
  const [loading, setLoading] = useState(true);
  const [generatingForCollege, setGeneratingForCollege] = useState(null);

  // Fetch real essay data from essay_slots table
  const fetchEssayData = async () => {
    if (!currentUser?.id || colleges.length === 0) {
      setLoading(false);
      return;
    }

    try {
      const collegeIds = colleges.map(c => c.id);

      const { data, error } = await supabase
        .from('essay_slots')
        .select('college_id, slot_type, prompt_text, word_limit, is_optional, essay_slot')
        .eq('user_id', currentUser.id)
        .in('college_id', collegeIds);

      if (error) throw error;

      console.log('📊 Fetched essay slots:', data);

      // Group essays by college
      const grouped = (data || []).reduce((acc, slot) => {
        if (!acc[slot.college_id]) {
          acc[slot.college_id] = {
            required: 0,
            optional: 0,
            prompts: [],
            totalWords: 0
          };
        }

        if (slot.is_optional) {
          acc[slot.college_id].optional++;
        } else {
          acc[slot.college_id].required++;
        }

        acc[slot.college_id].prompts.push({
          text: slot.prompt_text,
          wordLimit: slot.word_limit,
          type: slot.slot_type,
          number: slot.essay_slot,
          isOptional: slot.is_optional
        });

        acc[slot.college_id].totalWords += (slot.word_limit || 0);

        return acc;
      }, {});

      console.log('📊 Grouped essay data:', grouped);
      setEssayData(grouped);
    } catch (error) {
      console.error('Error fetching essay data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEssayData();
  }, [colleges, currentUser]);

  // Generate essay requirements for a college
  const handleGenerateEssays = async (college) => {
    if (!currentUser?.id) return;

    setGeneratingForCollege(college.id);
    try {
      await generateEssayRequirements(college, currentUser.id, () => {
        if (onDataGenerated) onDataGenerated();
      });

      // Refetch essay data immediately after generation
      console.log('🔄 Refetching essay data after generation...');
      await fetchEssayData();
    } catch (error) {
      console.error('Error in handleGenerateEssays:', error);
    } finally {
      setGeneratingForCollege(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-xs text-gray-600">Loading essay data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Essay Requirements Grid */}
      <div>
        <h3 className="text-xl font-bold text-slate-900 mb-4">Essay Requirements</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {colleges.map(college => {
            const collegeEssays = essayData[college.id] || { required: 0, optional: 0, prompts: [], totalWords: 0 };
            const collegeName = college.global_colleges?.name || college.name;
            const hasEssays = collegeEssays.required + collegeEssays.optional > 0;

            return (
              <div key={college.id} className="bg-white rounded-lg border-2 border-slate-900 shadow-sm p-4">
                <h4 className="font-bold text-slate-900 mb-3">{collegeName}</h4>

                {hasEssays ? (
                  <>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Required Essays</span>
                        <span className="font-semibold text-slate-900">{collegeEssays.required}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Optional Essays</span>
                        <span className="font-medium text-slate-700">{collegeEssays.optional}</span>
                      </div>
                      <div className="pt-2 border-t border-slate-200">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-600">Total Words</span>
                          <span className="font-bold text-amber-600">
                            {collegeEssays.totalWords.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Essay prompts */}
                    {collegeEssays.prompts.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-slate-200">
                        <p className="text-xs font-semibold text-slate-700 mb-2">Prompts:</p>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {collegeEssays.prompts.slice(0, 3).map((prompt, idx) => (
                            <div key={idx} className="text-xs text-slate-600">
                              <span className="font-medium">
                                {prompt.isOptional ? 'Optional' : 'Required'} {prompt.number}:
                              </span>{' '}
                              <span className="line-clamp-2">
                                {prompt.text}
                              </span>
                              {prompt.wordLimit && (
                                <span className="text-amber-600 font-medium ml-1">
                                  ({prompt.wordLimit} words)
                                </span>
                              )}
                            </div>
                          ))}
                          {collegeEssays.prompts.length > 3 && (
                            <p className="text-xs text-slate-500 italic">
                              +{collegeEssays.prompts.length - 3} more...
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 italic">
                      No essay requirements yet
                    </p>
                    <button
                      onClick={() => handleGenerateEssays(college)}
                      disabled={generatingForCollege === college.id}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-teal-600 text-white text-xs font-medium rounded hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {generatingForCollege === college.id ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Generate Essays
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Application Effort Estimation */}
      <div>
        <h3 className="text-xs font-semibold text-slate-900 mb-3">Application Effort Estimation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {colleges.map(college => {
            const collegeEssays = essayData[college.id] || { required: 0, optional: 0 };
            const effort = collegeEssays.required * 3 + collegeEssays.optional * 1.5; // Hours estimate
            const hasEssays = collegeEssays.required + collegeEssays.optional > 0;

            return (
              <div key={college.id} className="bg-white rounded-lg border-2 border-slate-300 shadow-sm p-3">
                <p className="text-xs font-medium text-slate-900 mb-2">
                  {college.global_colleges?.name || college.name}
                </p>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-600" />
                  <span className="text-xs text-slate-700">
                    {hasEssays ? `~${Math.round(effort)} hours estimated` : 'No essays yet'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Best Angle (when locked) */}
      {portfolioLocked && (
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-amber-600" />
            <h3 className="text-xs font-semibold text-amber-900">Your Best Angles</h3>
          </div>
          <p className="text-xs text-amber-700">
            Essay strategy and best angles will be generated when portfolio is locked.
            This analysis will be saved and available in the Essay Module.
          </p>
        </div>
      )}
    </div>
  );
};

export default EssaysTab;